<script setup lang="ts">
import { setSEO } from "~/utils/seoUtils";

const pageId = "contact-us";
const { data: page } = await useAsyncData(pageId, async () => await $fetch(`/api/content/pages/${pageId}`));

setSEO({ seo: page?.value?.seo, modifiedAt: page.value?.modified, publishedAt: page.value?.date });

</script>

<template>
  <main>
    <div
      class="tw-font-medium tw-mx-auto tw-mb-12 tw-max-w-screen-xl tw-px-4 tw-pt-12 tw-tracking-wider tw-w-full lg:tw-max-w-screen-lg md:tw-max-w-screen-md md:tw-pt-14"
    >
      <header
        class="tw-relative"
      >
        <h1
          class="md:tw-pt-12 tw-font-sans tw-font-bold tw-text-5xl md:tw-text-[5rem] tw-mb-5 tw-text-green-900 tw-uppercase tw-tracking-wide"
        >
          Contact Us
        </h1>
      </header>
    </div>
    <section
      class="md:tw-pb-20 tw-font-medium tw-leading-6 tw-pb-8 tw-text-green-900"
    >
      <header
        class="tw-text-sm tw-mx-auto tw-mb-16 tw-max-w-screen-xl tw-px-4 tw-text-green-900 tw-w-full lg:tw-text-xl lg:tw-max-w-screen-lg xl:tw-max-w-screen-xl 2xl:tw-max-w-screen-2xl md:tw-max-w-screen-md"
      >
        <h2
          class="lg:tw-text-5xl md:tw-mr-0 tw-font-sans tw-font-bold tw-text-4xl tw-mt-0 tw-ml-0 tw-mb-4 md:tw-mb-7 tw--mr-2.5 tw-text-left md:tw-text-center tw-text-green-900 tw-uppercase tw-tracking-wide md:tw-pb-2"
        >
          We’re Here For You!
        </h2>
        <div
          class="tw-max-w-3xl lg:md:tw-font-sans md:tw-mx-auto md:tw-uppercase tw-text-xl tw-leading-7 tw-text-green-900 tw-tracking-wider"
        >
          <p
            class="tw-font-sans tw-text-sm md:tw-text-xl tw-leading-5 md:tw-leading-7 tw-m-0 tw-text-left md:tw-text-center tw-text-green-900 tw-tracking-wider tw-pr-14 md:tw-pr-0"
          >
            Please reach out for any questions or concerns and we’ll get back to
            you as soon as possible. Due to a higher inquiry volume than normal,
            please allow 24-48 hours for our team to respond to your inquiry. For
            answers to frequently asked questions, visit our <NuxtLink href="/" class="tw-font-bold">FAQ</NuxtLink>.
          </p>
        </div>
      </header>
      <div
        class="tw-leading-6 tw-mx-auto tw-max-w-screen-xl tw-px-4 tw-text-green-900 tw-w-full lg:tw-max-w-screen-lg lg:tw-px-28 xl:tw-max-w-screen-xl 2xl:tw-max-w-screen-2xl md:tw-max-w-screen-md"
      >
        <div
          class="tw-flex tw-flex-wrap tw-text-sm tw-leading-snug tw--mx-5 tw-text-center tw-text-green-900"
        >
          <div
            class="md:tw-w-1/3 tw-text-sm tw-leading-5 tw-px-5 tw-pb-6 tw-text-center tw-text-green-900 tw-w-full"
          >
            <div
              class="md:tw-mb-4 tw-bg-white tw-flex tw-items-center tw-justify-center tw-h-24 tw-text-sm tw-leading-5 tw-mx-auto tw-mb-3.5 tw-text-center tw-text-green-900 tw-w-24"
              style="border-radius: 50%;"
            >
              <img
                src="https://content.nurturelife.com/wp-content/uploads/2020/12/ico-phone.svg"
                alt="ico-phone"
                width="43"
                class="tw-block tw-h-auto tw-text-sm tw-leading-5 tw-max-w-full tw-text-center tw-text-green-900 tw-align-middle"
              />
            </div>
            <h3
              class="md:tw-mb-5 tw-font-sans tw-font-bold tw-text-2xl tw-leading-tight tw-mx-0 tw-mt-0 tw-mb-6 tw-text-center tw-text-green-900 tw-uppercase"
            >
              Phone
            </h3>
            <span
              class="tw-block tw-text-sm tw-leading-5 tw-text-center tw-text-green-900"
            ><a
              target=""
              href="tel:3125171888"
              class="tw-cursor-pointer tw-text-sm tw-leading-5 tw-text-center"
              style="transition: color 0.3s ease-in 0s, background-color 0.3s ease-in 0s, border 0.3s ease-in 0s, opacity 0.3s ease-in 0s;"
            >312.517.1888</a
            ></span
            >
            <span
              class="tw-block tw-text-sm tw-leading-5 tw-text-center tw-text-green-900"
            >Mon – Fri, 9AM – 5PM CT</span
            >
          </div>
          <div
            class="md:tw-w-1/3 tw-text-sm tw-leading-5 tw-px-5 tw-pb-6 tw-text-center tw-text-green-900 tw-w-full"
          >
            <div
              class="md:tw-mb-4 tw-bg-white tw-flex tw-items-center tw-justify-center tw-h-24 tw-text-sm tw-leading-5 tw-mx-auto tw-mb-3.5 tw-text-center tw-text-green-900 tw-w-24"
              style="border-radius: 50%;"
            >
              <img
                src="https://content.nurturelife.com/wp-content/uploads/2020/12/ico-laptop.svg"
                alt="ico-laptop"
                width="66"
                class="tw-block tw-h-auto tw-text-sm tw-leading-5 tw-max-w-full tw-text-center tw-text-green-900 tw-align-middle"
              />
            </div>
            <h3
              class="md:tw-mb-5 tw-font-sans tw-font-bold tw-text-2xl tw-leading-tight tw-mx-0 tw-mt-0 tw-mb-6 tw-text-center tw-text-green-900 tw-uppercase"
            >
              Online
            </h3>
            <span
              class="tw-block tw-text-sm tw-leading-5 tw-text-center tw-text-green-900"
            ><a
              target=""
              href="mailto:support@nurturelife.com"
              class="tw-cursor-pointer tw-text-sm tw-leading-5 tw-text-center"
              style="transition: color 0.3s ease-in 0s, background-color 0.3s ease-in 0s, border 0.3s ease-in 0s, opacity 0.3s ease-in 0s;"
            >support@nurturelife.com</a
            ></span
            >
            <span
              class="tw-block tw-text-sm tw-leading-5 tw-text-center tw-text-green-900"
            >Mon – Fri, 9AM – 5PM CT</span
            >
          </div>
          <div
            class="md:tw-w-1/3 tw-text-sm tw-leading-5 tw-px-5 tw-pb-6 tw-text-center tw-text-green-900 tw-w-full"
          >
            <div
              class="md:tw-mb-4 tw-bg-white tw-flex tw-items-center tw-justify-center tw-h-24 tw-text-sm tw-leading-5 tw-mx-auto tw-mb-3.5 tw-text-center tw-text-green-900 tw-w-24"
              style="border-radius: 50%;"
            >
              <img
                src="https://content.nurturelife.com/wp-content/uploads/2020/12/ico-address.svg"
                alt="ico-address"
                width="60"
                class="tw-block tw-h-auto tw-text-sm tw-leading-5 tw-max-w-full tw-text-center tw-text-green-900 tw-align-middle"
              />
            </div>
            <h3
              class="md:tw-mb-5 tw-font-sans tw-font-bold tw-text-2xl tw-leading-tight tw-mx-0 tw-mt-0 tw-mb-6 tw-text-center tw-text-green-900 tw-uppercase"
            >
              Address
            </h3>
            <span
              class="tw-block tw-text-sm tw-leading-5 tw-text-center tw-text-green-900"
            >358 W Ontario St #1A<br
              class="tw-text-sm tw-leading-5 tw-text-center tw-text-green-900"
            />
              Chicago, IL 60654</span
            >
          </div>
        </div>
      </div>
    </section>
    <section
      class="md:tw-pb-20 tw-font-medium tw-leading-6 tw-pb-8 tw-text-green-900"
    >
      <header
        class="tw-font-sans tw-font-medium tw-text-base tw-mx-auto tw-mb-5 tw-px-4 tw-text-center tw-text-green-900 tw-uppercase tw-w-full lg:tw-text-xl lg:tw-max-w-screen-lg xl:tw-max-w-screen-xl 2xl:tw-max-w-screen-2xl md:tw-mb-16 md:tw-max-w-screen-md"
        style="line-height: 1.36;"
      ></header>
      <div
        class="tw-font-medium tw-leading-6 tw-mx-auto tw-max-w-screen-xl tw-px-4 tw-text-green-900 tw-w-full lg:tw-max-w-screen-lg lg:tw-px-16 xl:tw-max-w-screen-xl 2xl:tw-max-w-screen-2xl md:tw-max-w-screen-md"
      ></div>
    </section>
  </main>


</template>

<style scoped>

</style>
